import { useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { RefreshCw, Users, AlertCircle } from 'lucide-react';
import { adminFetch, AdminApiError } from '@/providers/admin-api';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';

/**
 * GHL journey backfill — an audited repair for users whose journey watermark
 * has fallen behind their real onboarding phase.
 *
 * WHY THIS UI EXISTS. The endpoint has existed since the 2026-08-10 empty-CRM
 * incident and had NO way to reach it: no button, no script. Running it meant
 * pasting a fetch into the DevTools console with a hand-extracted Cognito
 * token — which fails in three separate ways an operator cannot be expected to
 * debug (the cached idToken expires after ~1h; the refresh token expires too;
 * and `localStorage` usually holds SEVERAL idToken keys, so the obvious
 * `.find()` silently sends a previous session's token). All three happened on
 * 2026-08-25 while trying to repair four real users.
 *
 * `adminFetch` removes the whole class: it resolves the token through the live
 * Cognito session (never `localStorage`) and fails closed with a clear 401
 * rather than sending `Bearer null` — the fix from incident 2026-05-22.
 *
 * WHAT IT REPAIRS. Contacts normally reach GHL when a user crosses a journey
 * seam. Users already PAST every seam never re-fire one, so a watermark that
 * drifted stays wrong forever. Measured on PROD 2026-08-25: four users sat
 * BELOW their real phase, two of them for FOURTEEN DAYS — so GHL was nudging
 * them to finish steps they had already finished.
 *
 * SAFE BY CONSTRUCTION: it re-runs the ordinary `syncJourneyPhase` path, so
 * every consent, residency and carve gate still applies inside
 * `enqueueContactSync`. The watermark is forward-only (`GREATEST`), so it
 * cannot regress anyone, and re-running is idempotent.
 */

interface BackfillResult {
  scanned: number;
  /** On a dry run this is what a LIVE run would enqueue — the same gate code
   *  produced it. It was hard-coded 0 until 2026-08-26. */
  enqueued: number;
  /** Why the rest were not enqueued. Shown because `scanned - enqueued` cannot
   *  tell "deliberately suppressed" from "blew up", and an operator cannot act
   *  on a number with no reason attached. */
  skipped?: {
    marketing_opt_out: number;
    not_us_eligible: number;
    sync_disabled: number;
    error: number;
  };
  dry_run: boolean;
  sync_enabled?: boolean;
}

const SKIP_LABELS: Record<string, string> = {
  marketing_opt_out: 'opted out of marketing',
  not_us_eligible: 'not US-eligible (no +1 phone / non-English locale)',
  sync_disabled: 'GHL sync disabled',
  error: 'errored',
};

const DEFAULT_LIMIT = 50;

export function GhlBackfillCard() {
  const apiUrl = useApiUrl();
  const { hasPermission, loaded } = useAdminPermissions();
  const canWrite = hasPermission('settings.write');

  // Dry-run defaults ON. This writes to the CRM; the operator opts IN to that.
  const [dryRun, setDryRun] = useState(true);
  const [limit, setLimit] = useState(String(DEFAULT_LIMIT));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const parsedLimit = Number(limit);
  const limitValid = Number.isInteger(parsedLimit) && parsedLimit >= 1 && parsedLimit <= 500;

  async function run() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminFetch(`${apiUrl}/v1/admin/marketing/ghl-backfill`, {
        method: 'POST',
        body: JSON.stringify({ dry_run: dryRun, limit: parsedLimit }),
      });
      setResult((await res.json()) as BackfillResult);
    } catch (e) {
      // Surface the STATUS, not just a message: 401 means the session lapsed
      // (log in again), 403 means the admin lacks settings.write. Collapsing
      // those into one "failed" string is what made the console route so hard
      // to debug.
      setError(
        e instanceof AdminApiError
          ? `HTTP ${e.status}: ${e.message}`
          : (e as Error)?.message || 'Backfill failed',
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-lg border bg-card shadow-sm">
      <div className="flex items-start gap-3 border-b p-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Users className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">GHL journey backfill</h3>
          <p className="text-sm text-muted-foreground">
            Re-syncs users whose journey phase in GHL has fallen behind their real onboarding
            progress — they are being messaged to finish steps they already finished. Idempotent
            and forward-only: it can never move anyone backwards.
          </p>
        </div>
      </div>

      <div className="space-y-5 p-6">
        {!canWrite && loaded && (
          <div className="rounded-md border border-yellow-400 bg-yellow-50 p-3 text-sm text-yellow-900">
            You have read-only access. The <code>settings.write</code> permission is required to
            run the backfill.
          </div>
        )}

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="backfill-limit" className="mb-1 block text-sm font-medium">
              Max users to scan
            </label>
            <input
              id="backfill-limit"
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              disabled={running || !canWrite}
              className="block w-32 rounded-md border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            {!limitValid && (
              <p className="mt-1 flex items-center gap-1 text-xs text-yellow-700">
                <AlertCircle className="h-3 w-3" />
                Must be a whole number between 1 and 500.
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              disabled={running || !canWrite}
              className="h-4 w-4 rounded border"
            />
            <span>
              Dry run <span className="text-muted-foreground">(report only, no CRM writes)</span>
            </span>
          </label>

          <button
            type="button"
            onClick={run}
            disabled={running || !canWrite || !limitValid}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {running ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {running ? 'Running…' : dryRun ? 'Preview' : 'Run backfill'}
          </button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {result && (
          <div className="rounded-md border bg-muted/40 p-3 text-sm">
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <span>
                Scanned: <strong>{result.scanned}</strong>
              </span>
              <span>
                Enqueued: <strong>{result.enqueued}</strong>
              </span>
              <span className={result.dry_run ? 'text-yellow-700' : 'text-green-700'}>
                {result.dry_run
                  ? 'Dry run — real gates, nothing written'
                  : 'Live run — changes applied'}
              </span>
            </div>

            {result.sync_enabled === false && (
              <p className="mt-2 text-yellow-700">
                GHL sync is <strong>disabled</strong> in this environment, so nothing can be
                enqueued. Do not read 0 as “no users need repair”.
              </p>
            )}

            {result.skipped && Object.values(result.skipped).some((n) => n > 0) && (
              <ul className="mt-2 space-y-0.5 text-muted-foreground">
                {Object.entries(result.skipped)
                  .filter(([, n]) => n > 0)
                  .map(([reason, n]) => (
                    <li key={reason}>
                      <strong>{n}</strong> skipped — {SKIP_LABELS[reason] ?? reason}
                    </li>
                  ))}
              </ul>
            )}

            {result.dry_run && result.enqueued > 0 && (
              <p className="mt-2 text-muted-foreground">
                Untick “Dry run” and run again to apply these {result.enqueued}.
              </p>
            )}
            {result.dry_run && result.enqueued === 0 && result.sync_enabled !== false && (
              <p className="mt-2 text-muted-foreground">
                Nothing to apply — every scanned user is already in sync or is listed above.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
